import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import type {EventWithEventContainer} from "~/domainModels/eventContainers";
import type {EventContainerConstraint} from "~/domainModels/workspaceModules/eventContainersModule";

export type EventEditPanelColumnConfig = {
    isEditable: boolean;
    constraints?: Array<EventContainerConstraint>;
};

function getEventRefValue(
    event: EventWithEventContainer,
    refColumnId: Uuid | string,
    isAdditionalColumn: boolean,
    columnType: "OttopilotEvent" | "OttopilotEventContainer",
): string | null | undefined {
    if (columnType === "OttopilotEventContainer") {
        if (isAdditionalColumn === true) {
            return event.eventContainer.additionalColumnValues[refColumnId as Uuid] as string | null | undefined;
        }
        return (event.eventContainer as unknown as Record<string, unknown>)[refColumnId] as string | null | undefined;
    }
    // Event
    if (isAdditionalColumn === true) {
        return event.additionalColumnValues[refColumnId as Uuid] as string | null | undefined;
    }
    return (event as unknown as Record<string, unknown>)[refColumnId] as string | null | undefined;
}

function isSameCalendarDay(iso8601DateTime: string, now: Date): boolean {
    const d = new Date(iso8601DateTime);
    return (
        d.getFullYear() === now.getFullYear() &&
        d.getMonth() === now.getMonth() &&
        d.getDate() === now.getDate()
    );
}

function parseDurationToMs(duration: string): number | null {
    if (duration === "P1D") {
        return 24 * 60 * 60 * 1000;
    }
    const hoursMatch = duration.match(/^PT(\d+)H$/);
    if (hoursMatch !== null) {
        return parseInt(hoursMatch[1]!, 10) * 60 * 60 * 1000;
    }
    return null;
}

function evaluateEventConstraint(
    event: EventWithEventContainer,
    constraint: EventContainerConstraint,
    now: Date,
): boolean {
    if (constraint.type === "Time") {
        // Time constraints reference event fields (additionalColumnValues or system fields like createdAt)
        const refValue = getEventRefValue(
            event,
            constraint.refColumnId,
            constraint.isAdditionalColumn,
            "OttopilotEvent",
        );
        if (refValue === null || refValue === undefined || typeof refValue !== "string") {
            return false;
        }
        if (constraint.duration === "P1D") {
            return isSameCalendarDay(refValue, now);
        }
        const refTime = new Date(refValue).getTime();
        const durationMs = parseDurationToMs(constraint.duration);
        if (durationMs === null) {
            return false;
        }
        return now.getTime() - refTime < durationMs;
    }
    if (constraint.type === "Status") {
        const refValue = getEventRefValue(
            event,
            constraint.refColumnId,
            constraint.isAdditionalColumn,
            constraint.columnType,
        );
        if (refValue === null || refValue === undefined) {
            return true;
        }
        return constraint.disallowedStatuses.includes(refValue as Uuid) === false;
    }
    return true;
}

/**
 * Returns effective isEditable for an event edit panel column: base isEditable AND all constraints pass.
 */
export function getEventEditPanelColumnIsEditable(
    event: EventWithEventContainer,
    columnConfig: EventEditPanelColumnConfig,
    now: Date = new Date(),
    globalConstraints?: Array<EventContainerConstraint>,
): boolean {
    if (columnConfig.isEditable !== true) {
        return false;
    }
    const constraints = [
        ...(globalConstraints ?? []),
        ...(columnConfig.constraints ?? []),
    ];
    if (constraints.length === 0) {
        return true;
    }
    return constraints.every((c) => evaluateEventConstraint(event, c, now));
}
